import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import { FormikProvider } from 'formik'
import { Features } from 'generated/featureDefinitions'
import {
  ContactFragment,
  CreateProjectMutationVariables,
  CreateProjectmanagementProjectPayload,
  ProjectManagementFinancialDetailsFormDocument,
} from 'generated/resources/graphql'
import { compact, orderBy } from 'lodash'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { useActivities } from 'modules/core/lib/activities/provider'
import { dateUIToBackend } from 'modules/core/lib/dates'
import { HandleMutationResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { GeneralInformationStep } from 'modules/project_management/components/wizards/AddProjectWizard/0_GeneralInformationStep'
import { RolesSelectionStep } from 'modules/project_management/components/wizards/AddProjectWizard/1_RolesSelectionStep'
import { FinancialDetailsStep } from 'modules/project_management/components/wizards/AddProjectWizard/2_FinancialDetailsStep'
import { BudgetConfigurationStep } from 'modules/project_management/components/wizards/AddProjectWizard/3_BudgetConfigurationStep'
import { budgetTypesEnum } from 'modules/project_management/lib/constants'
import { DEFAULT_CURRENCY } from 'modules/project_management/lib/constants'
import { getMinutesFromDecimalHours } from 'modules/project_management/lib/formatHours'

import { CreateProjectProps } from './types'

type Props = {
  client?: ContactFragment
  locked?: boolean
  create: (dto: CreateProjectMutationVariables) => HandleMutationResult<
    Omit<CreateProjectmanagementProjectPayload, 'project'> & {
      project?: Partial<CreateProjectmanagementProjectPayload['project']> | null
    }
  >
  onClose: () => void
  onSuccess: (projectId: number) => void | Promise<void>
}

const AddProjectWizard = ({ client, locked, create, onClose, onSuccess }: Props) => {
  const {
    employee,
    company,
    access: { fullName, avatar: accessAvatar },
  } = useCurrentSession()

  const { currentActivity } = useActivities()

  const mainLegalEntity = orderBy(company.legalEntities, 'main', 'desc')[0]?.id
  const currentEmployeeId = employee?.id

  const { data: financialData } = useQuery(ProjectManagementFinancialDetailsFormDocument, {
    variables: {
      companyId: company.id,
      featureIds: [Features.projects_costs, Features.projects_budget, Features.projects_clients],
    },
  })

  const hasCostsFeature = !!financialData?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_costs
  )?.available
  const hasBudgetFeature = !!financialData?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_budget
  )?.available
  const hasClientsFeature = !!financialData?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_clients
  )?.available
  const hasTotalProfitabilityFeature =
    financialData?.features.featuresConnection.nodes.find(
      (node) => node.name === 'dev_projects_total_profitability'
    )?.enabled ?? false

  const form = useForm<CreateProjectProps>({
    defaultValues: {
      name: '',
      code: undefined,
      startDate: undefined,
      dueDate: undefined,
      status: 'active',
      projectRoles: [
        {
          role: 'owner',
          employee: {
            id: currentEmployeeId || 0,
            fullName: fullName,
            firstName: employee?.firstName || '',
            lastName: employee?.lastName || '',
            access: { avatar: accessAvatar },
          },
        },
      ],
      fixedCost: undefined,
      isBillable: true,
      currencyType: DEFAULT_CURRENCY,
      legalEntityId: mainLegalEntity?.toString() || '0',
      budgetType: budgetTypesEnum.withoutBudget,
      budgetAllocation: undefined,
      budgetAllocationCents: undefined,
      plannedCents: undefined,
      plannedMinutes: undefined,
      feeAmountCents: undefined,
      client: client ?? null,
    },
    validate: (data) => {
      const errorValidation: FormErrors<CreateProjectProps> = {}

      if (!data.name) {
        errorValidation.name = i18n.t('project_management.modals.error_messages.mandatory')
      }

      if (data.fixedCost) {
        if (isNaN(Number(data.fixedCost.replace(',', '.')))) {
          errorValidation.fixedCost = i18n.t(
            'project_management.forms.financial_details.fixed_costs.format_error'
          )
        }
        if (Number(data.fixedCost) < 0) {
          errorValidation.fixedCost = i18n.t(
            'project_management.forms.financial_details.fixed_costs.negative_error'
          )
        }
      }

      if (data.budgetType === budgetTypesEnum.fixedTimeBudget) {
        if (data.budgetAllocation !== undefined) {
          if (data.budgetAllocation === null || data.budgetAllocation <= 0) {
            errorValidation.budgetAllocation = i18n.t(
              'project_management.forms.financial_details.fixed_costs.negative_error'
            )
          } else if (isNaN(data.budgetAllocation)) {
            errorValidation.budgetAllocation = i18n.t(
              'project_management.forms.financial_details.fixed_costs.format_error'
            )
          }
        }
      }

      if (data.budgetType === budgetTypesEnum.fixedCostPlanning) {
        if (data.budgetAllocationCents !== undefined) {
          if (data.budgetAllocationCents === null) {
            errorValidation.budgetAllocationCents = i18n.t(
              'project_management.planning.validation_errors.field_is_mandatory'
            )
          } else if (isNaN(data.budgetAllocationCents)) {
            errorValidation.budgetAllocationCents = i18n.t(
              'project_management.forms.financial_details.fixed_costs.format_error'
            )
          } else if (data.budgetAllocationCents <= 0) {
            errorValidation.budgetAllocationCents = i18n.t(
              'project_management.forms.financial_details.fixed_costs.negative_error'
            )
          }
        }
      }
      if (data.budgetType === budgetTypesEnum.projectFixedCost) {
        if (data.plannedCents !== undefined) {
          if (data.plannedCents === null) {
            errorValidation.plannedCents = i18n.t(
              'project_management.planning.validation_errors.field_is_mandatory'
            )
          } else if (isNaN(data.plannedCents)) {
            errorValidation.plannedCents = i18n.t(
              'project_management.forms.financial_details.fixed_costs.format_error'
            )
          } else if (data.plannedCents <= 0) {
            errorValidation.plannedCents = i18n.t(
              'project_management.forms.financial_details.fixed_costs.negative_error'
            )
          }
        }
      }

      if (
        (data.budgetType === budgetTypesEnum.fixedCostPlanning ||
          data.budgetType === budgetTypesEnum.projectFixedCost) &&
        data.feeAmountCents
      ) {
        if (data.feeAmountCents <= 0) {
          errorValidation.feeAmountCents = i18n.t(
            'project_management.planning.validation_errors.field_higher_than_0'
          )
        }
        if (isNaN(data.feeAmountCents)) {
          errorValidation.feeAmountCents = i18n.t(
            'project_management.planning.validation_errors.field_is_number'
          )
        }
      }

      if (data.budgetType === budgetTypesEnum.totalBudget) {
        if (data.budgetAllocation !== undefined) {
          if (data.budgetAllocation === null) {
            errorValidation.budgetAllocation = i18n.t(
              'project_management.planning.validation_errors.field_is_mandatory'
            )
          } else if (isNaN(data.budgetAllocation)) {
            errorValidation.budgetAllocation = i18n.t(
              'project_management.forms.financial_details.fixed_costs.format_error'
            )
          } else if (data.budgetAllocation <= 0) {
            errorValidation.budgetAllocation = i18n.t(
              'project_management.forms.financial_details.fixed_costs.negative_error'
            )
          }
        }
      }

      return errorValidation
    },
    onSubmit: async (data: CreateProjectProps) => {
      const result = await create({
        // 1st step
        name: data.name,
        code: data.code,
        description: data.description,
        startDate: data.startDate ? dateUIToBackend(data.startDate) : undefined,
        dueDate: data.dueDate ? dateUIToBackend(data.dueDate) : undefined,
        status: data.status,
        // 2nd step
        projectAdmins: data.projectRoles
          .filter((pr) => pr.role === 'owner')
          .map((owner) => owner.employee.id),
        projectManagers: data.projectRoles
          .filter((pr) => pr.role === 'editor')
          .map((editor) => editor.employee.id),
        projectViewers: data.projectRoles
          .filter((pr) => pr.role === 'viewer')
          .map((viewer) => viewer.employee.id),
        // 3rd step
        fixedCostCents: data.fixedCost ? Number(data.fixedCost) * 100 : undefined,
        legalEntityId: Number(data.legalEntityId),
        isBillable: data.isBillable,
        // 4th step
        budgetAllocation:
          data.budgetType === budgetTypesEnum.totalBudget ||
          data.budgetType === budgetTypesEnum.projectFixedCost
            ? 1 //TODO: delete after release of planned cents
            : data.budgetAllocation
            ? getMinutesFromDecimalHours(data.budgetAllocation)
            : undefined,
        budgetAllocationCents: data.budgetAllocationCents
          ? Number(data.budgetAllocationCents) * 100
          : undefined,
        feeAmountCents: data.feeAmountCents ? Number(data.feeAmountCents) * 100 : undefined,
        ...(data.client && { clientId: data.client.id }),
        budgetType:
          data.budgetType === budgetTypesEnum.totalBudget ||
          data.budgetType === budgetTypesEnum.projectFixedCost
            ? data.budgetType
            : null, // TODO: Remove this once we have migrated all budgets to the new budget strategy
        plannedCents: data.plannedCents ? Number(data.plannedCents) * 100 : undefined,
        plannedMinutes: data.plannedMinutes ? Number(data.plannedMinutes) * 60 : undefined,
      })

      const handled = handleResult(result, (errors) => {
        if (errors) {
          form.setErrors(errors)
        }

        onSubmitError(errors)
      })

      if (handled.success) {
        if (data.budgetType === budgetTypesEnum.fixedCostPlanning) {
          track(trackingEvents.projectManagement.wizard.AddProjectSubmitProjectWizard, {
            budget_type: data.isBillable ? 'Planned_cost_income' : 'Planned_cost',
          })
        } else {
          track(trackingEvents.projectManagement.wizard.AddProjectSubmitProjectWizard, {
            budget_type: data.budgetType,
          })
        }
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        if (handled.data.project?.id) {
          onSuccess(handled.data.project.id)
        }
      }

      return handled
    },
  })

  const steps: WizardStep<CreateProjectProps>[] = compact([
    {
      title: i18n.t('project_management.modals.create_project.wizard.step_one.title'),
      nextText: i18n.t('common.next'),
      render: (props) => (
        <GeneralInformationStep
          {...props}
          clientReadOnly={!!client}
          hasClientsFeature={hasClientsFeature}
          companyId={company.id}
        />
      ),
      isCompleted: (data) => !!data.name && !form.errors.name && !form.errors.code,
    },
    {
      title: i18n.t('project_management.modals.create_project.wizard.step_two.title'),
      nextText: i18n.t('common.next'),
      render: (props) => <RolesSelectionStep {...props} />,
      isCompleted: (data) => data.projectRoles.length > 0,
    },
    {
      title: i18n.t('project_management.modals.create_project.wizard.financial_step.title'),
      nextText: i18n.t('common.next'),
      render: (props) => (
        <FinancialDetailsStep
          {...props}
          hasCostsFeature={hasCostsFeature}
          details={financialData}
        />
      ),
      isCompleted: (_) => !form.errors.fixedCost,
    },
    {
      title: i18n.t('project_management.modals.create_project.wizard.step_three.title'),
      nextText: i18n.t('project_management.modals.create_project.submit'),
      render: (props) => (
        <BudgetConfigurationStep
          {...props}
          hasBudgetFeature={hasBudgetFeature}
          hasCostsFeature={hasCostsFeature}
          hasTotalProfitabilityFeature={hasTotalProfitabilityFeature}
        />
      ),
      isCompleted: (data) =>
        !form.isSubmitting &&
        (data.budgetType === budgetTypesEnum.withoutBudget ||
          !!data.budgetAllocation ||
          !!data.plannedCents ||
          !!data.plannedMinutes ||
          !!data.budgetAllocationCents) &&
        !form.errors.budgetAllocation &&
        !form.errors.budgetAllocationCents,
    },
  ])

  const onSubmitError = (errors: Record<string, string>) => {
    app.setFlash({
      type: 'wrong',
      message:
        errors?.errors ||
        Object.entries(errors)
          .map(([_, value]) => `${value}`)
          .join(', '),
    })
  }

  const formHandleSubmit = form.handleSubmit
  const onSubmit = useUnsafeCallback(() => formHandleSubmit(), [formHandleSubmit])

  if (!employee) return null
  if (!mainLegalEntity) return null

  return (
    <FormikProvider value={form}>
      <Wizard.Modal
        title={i18n.t('project_management.modals.create_project.title')}
        icon="tasks_management"
        steps={steps}
        // Hack for Wizard. Wizard have a bug causing closes when opening an activity.
        // For that, we can fix it temporally locking the modal a if we have an activity open.
        locked={locked || !!currentActivity}
        initialData={form.initialValues}
        onSubmit={onSubmit}
        onClose={onClose}
        onDataChanged={form.setValues}
      />
    </FormikProvider>
  )
}

export default AddProjectWizard
